import * as React from 'react';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { Tree } from '@shared/ui-react/Tree';
import { childFilterPresets, Tree as TreeNative } from '@shared/ui-native/Tree';
import './FiletreeSelectReact.scss';
import { Button } from '@shared/ui-react/Button';
import { FileTreeService } from '@shared/services/FileTreeService';
import { cs } from '@shared/utils/utils-classname';
import clamp = utils.clamp;

export class FiletreeSelectReact extends React.Component<
    {
        treeData?: any;
        selectable?: 'folder' | 'file' | 'all';
        selectedId: string | number;
        onChange?: (id: string) => void;
        formFieldName?: string;
        storageKey?: string;
        includeRoot?: boolean;
        hideEmptyFolders?: boolean;
        hideExpandOptions?: boolean;
        canClearValue?: boolean;
        showSelectedIcon?: boolean;
        hideNonPresentable?: boolean;
        headerControlsFilterOnly?: boolean;
        fetchAccess?: boolean;
        childFilter?: Function;
        nullValue?: string; // eg. '-1'
        useUuid?: boolean;
        disabled?: boolean;
        disabledIds?: number[];
        disabledUuids?: string[];
        width?: string;
        onTreeRendered?: (tree: TreeNative) => void;
        noIcons?: boolean;
    },
    {}
> {
    static defaultProps = {
        selectable: 'all',
        includeRoot: true,
        canClearValue: false,
        hideEmptyFolders: false,
        hideExpandOptions: false,
        showSelectedIcon: true,
        nullValue: '-1',
        width: '100%',
    };

    state = {
        isOpen: false,
        selectedIdUncontrolled: null,
    };

    treeRef: Tree = null;
    dropdownRef: HTMLDivElement = null;

    componentDidMount() {
        if (!this.isControlled) {
            this.setState({
                selectedIdUncontrolled: this.props.selectedId,
            });
        }

        setTimeout(() => {
            if (this.treeRef) {
                this.treeRef.tree.getSelected()?.scrollTo();
            }
        }, 150);
    }

    componentDidUpdate(prevProps) {
        if (this.props.selectedId != prevProps.selectedId) {
            if (!this.isControlled) {
                this.setState({
                    selectedIdUncontrolled: this.props.selectedId,
                });
            }
        }
    }

    render() {
        const { treeData, selectedId: selectedIdControlled, showSelectedIcon, selectable, formFieldName, storageKey, nullValue, useUuid } = this.props;
        let selectedId = this.isControlled ? selectedIdControlled : this.state.selectedIdUncontrolled;
        if (nullValue && selectedId == nullValue) {
            selectedId = null;
        }
        if (typeof selectedId == 'number') {
            selectedId = String(selectedId);
        }

        const selectedNode = useUuid ? FileTreeService.findNodeByUuid(treeData, selectedId) : FileTreeService.findNodeById(treeData, selectedId);
        const selectedLabel = selectedNode ? selectedNode.name : '---';
        let selectedFileClass = null;
        if (showSelectedIcon) {
            selectedFileClass = selectable === 'folder' ? 'folder_icon' : selectedNode ? 'file_icon_default file_icon_' + utils.getExtensionFromFilename(selectedNode.name) : null;
        }

        const minWidth = this.dropdownRef ? clamp(320, this.dropdownRef.parentElement.offsetWidth, Math.min(700, window.innerWidth)) : 320;

        let childFilter = null;
        if (selectable === 'folder') {
            childFilter = childFilterPresets.onlyFolders;
        } else if (typeof this.props.childFilter === 'function') {
            childFilter = this.props.childFilter;
        }

        return (
            <div className='btn-group' style={{ width: this.props.width, overflow: 'hidden' }}>
                <Dropdown
                    closeOnBodyClick={false}
                    isOpen={this.state.isOpen}
                    onToggle={this.toggle}
                    triggerStyle={{ flexGrow: 1, overflow: 'hidden' }}
                    className='filetree-select-react__button ik-button-secondary'
                    disabled={this.props.disabled}
                    lazyRender={true}
                >
                    <div className='flex-row ai-center' ref={el => (this.dropdownRef = el)}>
                        {selectedFileClass && <div className={'filetree-select-react__icon ' + selectedFileClass}></div>}
                        <span className='ellipsis'>{selectedLabel}</span>
                        <i className='iconf-caret' />
                        {formFieldName && <input type='hidden' name={formFieldName} value={selectedId || nullValue} />}
                    </div>
                    <div
                        className={cs('filetree-select-react__body', { 'tree-no-icons': this.props.noIcons })}
                        style={{ minWidth, maxWidth: window.innerWidth < 800 ? '100vw' : '50vw' /* needs to support 100vw for mobile */ }}
                    >
                        <Tree
                            treeData={treeData}
                            includesFiles={selectable !== 'folder'}
                            selectedId={useUuid ? null : selectedId}
                            selectedUuid={useUuid ? selectedId : null}
                            selectFallbackToRoot={false}
                            onSelect={node => this.onChange(node, useUuid)}
                            headerControls={true}
                            onRender={this.onTreeRendered}
                            childFilter={childFilter}
                            storageKey={storageKey}
                            showRoot={this.props.includeRoot}
                            hideEmptyFolders={this.props.hideEmptyFolders}
                            hideEmptyFoldersAlways={this.props.hideEmptyFolders}
                            hideNonPresentable={this.props.hideNonPresentable}
                            ref={el => (this.treeRef = el)}
                            expandOnStart='all'
                            headerControlsFilterOnly={this.props.headerControlsFilterOnly}
                            hideExpandOptions={this.props.hideExpandOptions}
                        />
                    </div>
                </Dropdown>
                {this.props.canClearValue && selectedId?.length ? <Button secondary small icon='iki iki-cross' onClick={this.clearSelection} style={{ flexShrink: 0 }} /> : null}
            </div>
        );
    }

    toggle = _.debounce(s => {
        this.setState({ isOpen: s });

        setTimeout(() => {
            if (s && this.treeRef) {
                this.treeRef.tree.focusFilter();
                this.treeRef.tree.highlightNext();
            }
        }, 10);
    }, 10);

    onChange = (node, useUuid) => {
        this.setState({ isOpen: false });
        this.setSelectedId(useUuid ? node.uuid : node.id);
    };

    clearSelection = () => {
        this.setSelectedId(null);
    };

    setSelectedId = (id: string | number) => {
        if (typeof id == 'number') {
            id = String(id);
        }

        if (this.isControlled) {
            this.props.onChange(id);
        } else {
            this.setState({ selectedIdUncontrolled: id });
        }
    };

    onTreeRendered = (tree: TreeNative) => {
        if (this.props.fetchAccess) {
            tree.fetchAccessList(true);
        }

        if (this.props.selectable === 'all') return;

        tree.each(
            node => {
                const type = node.isFolder() ? 'folder' : 'file';
                node.state.enabled = this.props.selectable === type;

                if (Array.isArray(this.props.disabledIds)) {
                    node.state.enabled = !this.props.disabledIds.includes(node.id);
                } else if (Array.isArray(this.props.disabledUuids)) {
                    node.state.enabled = !this.props.disabledUuids.includes(node.uuid);
                }
                node.view.renderEnabled();
            },
            true,
            true
        );

        // do this last so 'enabled' state can be overriden
        if (typeof this.props.onTreeRendered === 'function') {
            this.props.onTreeRendered(tree);
        }
    };

    get isControlled() {
        return typeof this.props.onChange === 'function';
    }
}
